import type { ToolCallMessagePart as ToolCallContentPart } from "@assistant-ui/react";
import type { ReadonlyJSONObject } from "../jsonTypes";
import { z } from "zod";
import {
  FrontPosition,
  StreamEvent,
  TextReplaceToolEvent,
  TextReplaceToolEventSchema,
  ToolCrashEventSchema,
  ToolErrorEventSchema,
  ToolEvent,
  ToolEventEndEvent,
  ToolProblemResultEvent,
  ToolStartEvent,
  ToolStartEventSchema,
  ToolSuccessEvent,
  ToolSuccessEventSchema,
} from "../../event";
import { TToolCallBackendData } from "../../RuntimeProvider";
import {
  ThreadAssistantContentPart,
  ToolCallsRecord,
  appendTextToCurrentText,
  insertToolAtPosition,
  repositionEndTools,
  updateTextContent,
  updateToolInPlace,
  updateToolPositionInContent,
} from "./positionHandler";

export type ContentState = {
  content: ThreadAssistantContentPart[];
  currentText: string;
  toolCalls: ToolCallsRecord;
  backend_id: number | null;
};

/**
 * Determines the processor key to use for a given event
 * @param event - The stream event to analyze
 * @returns The appropriate processor key string or null if no matching processor found
 */
function determineProcessorKey(event: StreamEvent): string | null {
  if ("text" in event) {
    return "text";
  } else if ("type" in event) {
    return event.type;
  }
  return null;
}

/**
 * Type defining an event processor for a specific event type
 * @param event - The specific stream event to be processed
 * @param content - Current array of content parts to be updated
 * @param currentText - Current accumulated text string
 * @param toolCalls - Record of tool calls indexed by their IDs
 * @returns ContentState object with updated content, text, and tool calls
 */
type EventProcessor<T extends StreamEvent> = (
  event: T,
  content: ThreadAssistantContentPart[],
  currentText: string,
  toolCalls: ToolCallsRecord,
) => ContentState;

/**
 * Factory of event processors for each event type
 */
export const eventProcessors: Record<string, EventProcessor<any>> = {
  text: (event, content, currentText, toolCalls): ContentState => {
    return processTextEvent(event.text, content, currentText, toolCalls);
  },

  tool_start: (event, content, currentText, toolCalls): ContentState => {
    const validatedEvent = ToolStartEventSchema.parse(event);
    return processToolStartEvent(
      validatedEvent,
      content,
      currentText,
      toolCalls,
    );
  },

  tool_success: (event, content, currentText, toolCalls): ContentState => {
    const validatedEvent = ToolSuccessEventSchema.parse(event);
    return processToolSuccessEvent(
      validatedEvent,
      content,
      currentText,
      toolCalls,
    );
  },

  tool_error: (event, content, currentText, toolCalls): ContentState => {
    const validatedEvent = ToolErrorEventSchema.parse(event);
    return processToolErrorEvent(
      validatedEvent,
      content,
      currentText,
      toolCalls,
    );
  },

  tool_crash: (event, content, currentText, toolCalls): ContentState => {
    const validatedEvent = ToolCrashEventSchema.parse(event);
    return processToolErrorEvent(
      validatedEvent,
      content,
      currentText,
      toolCalls,
    );
  },

  text_replace_tool: (event, content, currentText, toolCalls): ContentState => {
    const validatedEvent = TextReplaceToolEventSchema.parse(event);
    return processTextReplaceToolEvent(
      validatedEvent,
      content,
      currentText,
      toolCalls,
    );
  },
};

/**
 * Processes a text event and updates content
 * @param text - The text string to be added to the content
 * @param content - Current array of content parts to be updated
 * @param currentText - Current accumulated text string
 * @param toolCalls - Record of tool calls indexed by their IDs
 * @returns Updated ContentState with the text added to content
 */
function processTextEvent(
  text: string,
  content: ThreadAssistantContentPart[],
  currentText: string,
  toolCalls: ToolCallsRecord,
): ContentState {
  const updatedText = appendTextToCurrentText(currentText, text);

  const updatedContent = updateTextContent(content, text, updatedText);

  const finalContent = repositionEndTools(updatedContent);

  return {
    content: finalContent,
    currentText: updatedText,
    toolCalls,
    backend_id: null,
  };
}

/**
 * Processes a tool start event
 * @param event - Tool start event data
 * @param content - Current array of content parts
 * @param currentText - Current accumulated text
 * @param toolCalls - Record of tool calls indexed by their IDs
 * @returns Updated ContentState with the new tool call added
 */
function processToolStartEvent(
  event: ToolStartEvent,
  content: ThreadAssistantContentPart[],
  currentText: string,
  toolCalls: ToolCallsRecord,
): ContentState {
  currentText = "";

  const toolCallId = event.tool_id;
  const toolData = createToolStartData(event);
  const newToolCall = createToolCallPart(event, toolCallId, toolData);

  const updatedToolCalls = {
    ...toolCalls,
    [toolCallId]: newToolCall,
  };

  return {
    content: [...content, newToolCall],
    currentText,
    toolCalls: updatedToolCalls,
    backend_id: null,
  };
}

/**
 * Creates tool data for a tool start event
 * @param event - Tool start event containing tool information
 * @returns Structured tool data object for the backend
 */
function createToolStartData(
  event: z.infer<typeof ToolStartEventSchema>,
): TToolCallBackendData {
  return {
    backendToolName: event.tool_name,
    isFirstTool: true,
    front_position: event.front_position || "at_start",
    data: null,
  };
}

/**
 * Creates a tool call part
 * @param event - Event data containing tool information
 * @param toolCallId - Unique identifier for the tool call
 * @param toolData - Backend tool data including position and metadata
 * @returns Structured tool call content part
 */
function createToolCallPart(
  event: ToolEvent,
  toolCallId: string,
  toolData: TToolCallBackendData,
): ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData> {
  return {
    type: "tool-call",
    toolCallId,
    toolName: event.front_component_name || "UnknownTool",
    args: {} as ReadonlyJSONObject,
    argsText: "",
    result: toolData,
    ...(event.type === "tool_error" || event.type === "tool_crash"
      ? { isError: true }
      : {}),
  };
}

/**
 * Processes a tool success event
 * @param event - Tool success event data
 * @param content - Current array of content parts
 * @param currentText - Current accumulated text
 * @param toolCalls - Record of tool calls indexed by their IDs
 * @returns Updated ContentState with the successful tool call
 */
function processToolSuccessEvent(
  event: ToolSuccessEvent,
  content: ThreadAssistantContentPart[],
  currentText: string,
  toolCalls: ToolCallsRecord,
): ContentState {
  const toolCallId = event.tool_id;

  if (!toolCalls[toolCallId]) {
    return { content, currentText, toolCalls, backend_id: null };
  }

  const { updatedToolCall, updatedToolCalls, positionChanged, newPosition } =
    updateToolCall(event, toolCalls, toolCallId);

  const updatedContent = positionChanged
    ? updateToolPositionInContent(
        content,
        toolCallId,
        updatedToolCall,
        newPosition,
      )
    : updateToolInPlace(content, toolCallId, updatedToolCall);

  return {
    content: updatedContent,
    currentText,
    toolCalls: updatedToolCalls,
    backend_id: null,
  };
}

/**
 * Updates a tool call based on an event
 * @param event - Event data containing updated tool information
 * @param toolCalls - Current record of tool calls
 * @param toolCallId - ID of the tool call to update
 * @returns Object containing updated tool call, toolCalls record, position change status and new position
 */
function updateToolCall(
  event: ToolEventEndEvent,
  toolCalls: ToolCallsRecord,
  toolCallId: string,
): {
  updatedToolCall: ToolCallContentPart<
    ReadonlyJSONObject,
    TToolCallBackendData
  >;
  updatedToolCalls: ToolCallsRecord;
  positionChanged: boolean;
  newPosition: FrontPosition;
} {
  const currentToolCall = toolCalls[toolCallId];
  const currentToolData = currentToolCall.result as TToolCallBackendData;

  const oldPosition = currentToolData?.front_position;
  const newPosition = determineNewPosition(event, oldPosition);
  const positionChanged = oldPosition !== newPosition;

  const toolData = createUpdatedToolData(event, currentToolData, newPosition);

  const updatedToolCall = createUpdatedToolCall(
    event,
    currentToolCall,
    toolData,
  );

  const updatedToolCalls = {
    ...toolCalls,
    [toolCallId]: updatedToolCall,
  };

  return {
    updatedToolCall,
    updatedToolCalls,
    positionChanged,
    newPosition,
  };
}

/**
 * Determines the new position for a tool
 * @param event - Event containing position information
 * @param oldPosition - Previous position of the tool
 * @returns The new front position for the tool
 */
function determineNewPosition(
  event: ToolEventEndEvent,
  oldPosition?: FrontPosition,
): FrontPosition {
  if (event.type === "tool_success") {
    return event.front_position || oldPosition || "in_conversation_tool_end";
  } else {
    return event.front_position || oldPosition || "at_end";
  }
}

/**
 * Creates updated tool data
 * @param event - Event containing tool data information
 * @param currentToolData - Existing tool data to be updated
 * @param newPosition - New position for the tool
 * @returns Updated tool data object
 */
function createUpdatedToolData(
  event: ToolEventEndEvent,
  currentToolData: TToolCallBackendData,
  newPosition: FrontPosition,
): TToolCallBackendData {
  if (event.type === "tool_success") {
    return {
      backendToolName: event.tool_name,
      isFirstTool: currentToolData?.isFirstTool || false,
      front_position: newPosition,
      data: event.front_component_data,
    };
  } else {
    return {
      backendToolName: event.tool_name,
      isFirstTool: currentToolData?.isFirstTool || false,
      front_position: newPosition,
      data: event.front_component_data,
      error: event.error,
    };
  }
}

/**
 * Creates an updated tool call
 * @param event - Event containing tool call information
 * @param currentToolCall - Existing tool call to be updated
 * @param toolData - New tool data to apply
 * @returns Updated tool call content part
 */
function createUpdatedToolCall(
  event: ToolEventEndEvent,
  currentToolCall: ToolCallContentPart<
    ReadonlyJSONObject,
    TToolCallBackendData
  >,
  toolData: TToolCallBackendData,
): ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData> {
  return {
    ...currentToolCall,
    args: event.parameters || {},
    argsText: JSON.stringify(event.parameters || {}),
    result: toolData,
    toolName: event.front_component_name || currentToolCall.toolName,
    ...(event.type === "tool_error" || event.type === "tool_crash"
      ? { isError: true }
      : {}),
  };
}

/**
 * Processes a tool error event
 * @param event - Tool error or crash event data
 * @param content - Current array of content parts
 * @param currentText - Current accumulated text
 * @param toolCalls - Record of tool calls indexed by their IDs
 * @returns Updated ContentState with the error information added
 */
function processToolErrorEvent(
  event: ToolProblemResultEvent,
  content: ThreadAssistantContentPart[],
  currentText: string,
  toolCalls: ToolCallsRecord,
): ContentState {
  const toolCallId = event.tool_id;

  if (!toolCalls[toolCallId]) {
    return { content, currentText, toolCalls, backend_id: null };
  }

  const { updatedToolCall, updatedToolCalls, positionChanged, newPosition } =
    updateToolCall(event, toolCalls, toolCallId);

  const updatedContent = positionChanged
    ? updateToolPositionInContent(
        content,
        toolCallId,
        updatedToolCall,
        newPosition,
      )
    : updateToolInPlace(content, toolCallId, updatedToolCall);

  return {
    content: updatedContent,
    currentText,
    toolCalls: updatedToolCalls,
    backend_id: null,
  };
}

/**
 * Processes a text replace tool event
 * @param event - Text replace tool event data
 * @param content - Current array of content parts
 * @param currentText - Current accumulated text (kept intact for position tracking)
 * @param toolCalls - Record of tool calls indexed by their IDs
 * @returns Updated ContentState with the text replaced in content and tool added
 */
function processTextReplaceToolEvent(
  event: TextReplaceToolEvent,
  content: ThreadAssistantContentPart[],
  currentText: string,
  toolCalls: ToolCallsRecord,
): ContentState {
  const toolCallId = event.tool_id;
  const toolData = createTextReplaceToolData(event);
  const newToolCall = createToolCallPart(event, toolCallId, toolData);

  // Insert the tool at the correct position, replacing the specified text
  const finalContent = insertToolAtPosition(
    content,
    event.parameters.original_text,
    newToolCall,
  );

  const updatedToolCalls = {
    ...toolCalls,
    [toolCallId]: newToolCall,
  };

  return {
    content: repositionEndTools(finalContent),
    currentText, // Keep currentText intact for position tracking
    toolCalls: updatedToolCalls,
    backend_id: null,
  };
}

/**
 * Creates tool data for a text replace tool event
 * @param event - Text replace tool event containing tool information
 * @returns Structured tool data object for the backend
 */
function createTextReplaceToolData(
  event: TextReplaceToolEvent,
): TToolCallBackendData {
  return {
    backendToolName: event.tool_name,
    isFirstTool: true,
    front_position: event.front_position,
    data: event.front_component_data,
  };
}

/**
 * Processes a stream event and updates the content accordingly
 * @param event - Stream event to process, could be text or tool-related
 * @param content - Current array of content parts (text, tools, etc.)
 * @param currentText - Current accumulated text string that is being built
 * @param toolCalls - Record of tool calls indexed by their IDs
 * @param backend_id - Backend message ID if available, used for tracking
 * @returns Updated state object containing content, currentText, toolCalls and backend_id
 */
export function processStreamEvent(
  event: StreamEvent,
  content: ThreadAssistantContentPart[],
  currentText: string,
  toolCalls: ToolCallsRecord,
  backend_id: number | null,
): ContentState {
  if ("type" in event && event.type === "end" && event.backend_id) {
    backend_id = event.backend_id;
  }

  const processorKey = determineProcessorKey(event);

  if (processorKey && eventProcessors[processorKey]) {
    const processedState = eventProcessors[processorKey](
      event,
      content,
      currentText,
      toolCalls,
    );
    return { ...processedState, backend_id };
  }

  return { content, currentText, toolCalls, backend_id };
}
